<template>
  <div class="send-cv-mode-selection-modal">
    <div class="modal-content-wrapper">
      <div class="main-content">
        <div class="modal-header">
          <h3 class="modal-title">בחירת מצב שליחת קורות חיים</h3>
          <p class="modal-subtitle">אנא בחר את המצב המתאים לשליחת קורות החיים:</p>
        </div>

        <div class="mode-options" :class="{ 'has-reco': showRecommendation }">
          <div
            class="mode-option"
            :class="{ 'selected': selectedMode === 'normal', 'default': highlightedMode === 'normal' }"
            @click="selectMode('normal')"
          >
            <div class="mode-icon">
              <i class="fas fa-file-alt"></i>
            </div>
            <div class="mode-content">
              <h4 class="mode-name">
                רגיל
                <span v-if="highlightedMode === 'normal'" class="default-badge">מומלץ</span>
              </h4>
              <p class="mode-description">שליחת קורות חיים מלאים עם כל הפרטים והמידע</p>
            </div>
          </div>

          <div
            class="mode-option"
            :class="{ 'selected': selectedMode === 'lite', 'default': highlightedMode === 'lite' }"
            @click="selectMode('lite')"
          >
            <div class="mode-icon">
              <i class="fas fa-file"></i>
            </div>
            <div class="mode-content">
              <h4 class="mode-name">
                לייט
                <span v-if="highlightedMode === 'lite'" class="default-badge">מומלץ</span>
              </h4>
              <p class="mode-description">שליחת קורות חיים מקוצרים עם המידע החיוני</p>
            </div>
          </div>

          <div
            class="mode-option"
            :class="{ 'selected': selectedMode === 'super_lite', 'default': highlightedMode === 'super_lite' }"
            @click="selectMode('super_lite')"
          >
            <div class="mode-icon">
              <i class="fas fa-file-text"></i>
            </div>
            <div class="mode-content">
              <h4 class="mode-name">
                סופר לייט
                <span v-if="highlightedMode === 'super_lite'" class="default-badge">מומלץ</span>
              </h4>
              <p class="mode-description">שליחת קורות חיים מינימליים עם הפרטים הבסיסיים בלבד</p>
            </div>
          </div>
        </div>

        <!-- Late-arriving recommendation (UI reviewer issue 9): the block appears
             IN PLACE when the API responds. No reserved height, no skeleton —
             the CTA row is sticky, so nothing becomes unreachable. Deliberate. -->
        <CvSendPatternExplanation
          v-if="showRecommendation"
          :recommendation="recommendation"
          :selectedMode="selectedMode"
        />

        <div class="modal-actions">
          <button
            class="button is-primary is-large"
            :disabled="!selectedMode"
            @click="confirmSelection"
          >
            <span class="icon">
              <i class="fas fa-paper-plane"></i>
            </span>
            <span>פתח טופס שליחה</span>
          </button>

          <button
            class="button is-light is-large"
            @click="cancel"
          >
            <span class="icon">
              <i class="fas fa-times"></i>
            </span>
            <span>ביטול</span>
          </button>
        </div>
      </div>

      <div class="sidebar-content">
        <div class="evaluation-section">
          <h4 class="evaluation-title">התאמת המועמד למשרה</h4>
          <div v-if="offerData && offerData.applicant && offerData.position" class="evaluation-content">
            <ModalEvaluate
              :applicant="offerData.applicant"
              :position="offerData.position"
            />
          </div>
          <div v-else class="no-data">
            <p>אין מידע זמין להתאמה</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import ModalEvaluate from '@/components/ModalEvaluate'
import CvSendPatternExplanation from '@/components/cv/CvSendPatternExplanation.vue'
import { isRealOfferId } from '@/store/modules/cvTextArtifacts'

// A flags request that never settles must not hold the pre-selection hostage.
// Ten seconds is far longer than the endpoint's real latency and far shorter
// than the 1-5 minute modal dwell.
export const FLAGS_TIMEOUT_MS = 10000

// The company's stored default, read straight off the offer payload — no store,
// no flag, no network. This is what the modal pre-selected before the feature
// existed, and it is what `data()` uses so the CTA is enabled on the very first
// render.
export const companyDefaultMode = offerData => {
  if (offerData && offerData.position && offerData.position.company) {
    return offerData.position.company.default_cv_sending_mode || 'normal'
  }
  return 'normal'
}

export default {
  name: 'SendCvModeSelectionModal',
  components: {
    ModalEvaluate,
    CvSendPatternExplanation
  },
  props: {
    payload: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      // SYNCHRONOUS, at the FIRST render: the pre-feature default. Doing this in
      // mounted() left `פתח טופס שליחה` disabled for the initial paint, and
      // doing it in the flags promise (R-5) left it disabled until the network
      // answered. A feature that ships OFF must not be able to disable the CTA
      // that existed before it.
      selectedMode: companyDefaultMode(this.payload.data || this.payload)
    }
  },
  computed: {
    offerData () {
      return this.payload.data || this.payload
    },
    defaultMode () {
      return companyDefaultMode(this.offerData)
    },
    // Flags reach the frontend ONLY through the API payload — never through
    // build-time $settings — so a flag flip needs no deploy.
    patternUiEnabled () {
      return this.$store.getters['cvTextArtifacts/uiEnabled'] === true
    },
    // `cv_send_recommendation` is present-but-null when CV_PATTERN_UI_ENABLED is
    // off, so the off state stays byte-comparable (API-9).
    recommendation () {
      return (this.offerData && this.offerData.cv_send_recommendation) || null
    },
    showRecommendation () {
      return this.patternUiEnabled && !!(this.recommendation && this.recommendation.mode)
    },
    // Exactly ONE card carries the highlight + `מומלץ` badge: two badges would
    // read as two recommendations. With the flag on it follows the recommended
    // mode; with the flag off it stays on the stored company default (A-1).
    recommendedMode () {
      if (!this.showRecommendation) return null
      return this.recommendation.mode
    },
    highlightedMode () {
      return this.recommendedMode || this.defaultMode
    }
  },
  created () {
    // Lifecycle bookkeeping, deliberately NOT in data(): neither value is read
    // by the template, and making them reactive only costs re-renders.
    this.isGone = false
    this.handedOff = false
    this.userInteracted = false
    this.flagsTimer = null
    this.generatedFor = null
  },
  mounted () {
    // The selection itself is already made (see data()). Deliberately NO
    // generation here either: if the recommendation turns out to differ,
    // generating for the default first would spend a booster call on a mode
    // nobody selected — the R-5 waste, in a different order.
    //
    // Once the flags land, upgrade to the recommendation and generate for it —
    // ONE generation, for the mode she will actually see selected. DEF-7: a
    // manually configured mode is always the recommendation, whatever its
    // confidence.
    this.withFlagsTimeout(this.$store.dispatch('cvTextArtifacts/fetchFlags', { vm: this }))
      .then(() => {
        // S-3: the flags request outlives a modal she closed while it was still
        // in flight. Selecting for a destroyed component is harmless, but the
        // generation it triggers starts a 5 s poller that beforeDestroy has
        // already swept — so it would poll forever with nothing to receive it.
        if (this.isGone || this._isDestroyed) return
        // She got there first: her click owns the SELECTION and the cards must
        // not move under her hand. The generation still has to happen, because
        // on a cold store her click could not start one — `generate` reads
        // CV_AI_TEXTS_ENABLED, which was still false at that moment.
        if (!this.userInteracted) this.selectedMode = this.highlightedMode
        this.triggerGeneration(this.selectedMode)
      })
      .catch(error => {
        // Timed out or failed: stay on the default that is already selected and
        // generate nothing. Off-state behavior, which is the safe one.
        console.warn('SendCvModeSelectionModal: CV flags unavailable, keeping the company default —',
          (error && error.message) || error)
      })
  },
  beforeDestroy () {
    this.isGone = true
    if (this.flagsTimer) clearTimeout(this.flagsTimer)
    this.flagsTimer = null
    // A HANDOFF keeps the session: the generation started here is exactly what
    // the CV modal opening in its place is waiting for, and both modals share
    // the offer-keyed store entry.
    //
    // S-3: an ABANDONED mode modal (ביטול, or the modal X) had no teardown at
    // all, so the poller `triggerGeneration` started outlived the component and
    // kept hitting the status endpoint every 5 s for the rest of the session —
    // once per abandoned open, plus once more per mode she clicked through.
    if (this.handedOff) return
    const offerId = this.offerData && this.offerData.id
    if (!offerId) return
    this.$store.dispatch('cvTextArtifacts/closeSession', offerId)
  },
  methods: {
    // Races `promise` against a timeout so a hanging flags request cannot leave
    // the modal waiting on an upgrade that will never arrive.
    withFlagsTimeout (promise) {
      return new Promise((resolve, reject) => {
        this.flagsTimer = setTimeout(() => {
          this.flagsTimer = null
          reject(new Error('cv_flags_timeout'))
        }, FLAGS_TIMEOUT_MS)
        promise.then(
          value => {
            if (this.flagsTimer) clearTimeout(this.flagsTimer)
            this.flagsTimer = null
            resolve(value)
          },
          error => {
            if (this.flagsTimer) clearTimeout(this.flagsTimer)
            this.flagsTimer = null
            reject(error)
          }
        )
      })
    },
    selectMode (mode) {
      this.userInteracted = true
      this.selectedMode = mode
      this.triggerGeneration(mode)
    },
    // Generation starts here — before the CV modal even opens — so the text has
    // the 1-5 minute modal dwell time to arrive. Nothing on screen waits for it.
    triggerGeneration (mode) {
      if (!mode) return
      // The flags handler and an early click can both land on the SAME mode
      // (she clicked the recommended card before the flags resolved). One
      // generation per mode, per modal.
      if (this.generatedFor === mode) return
      const offerId = this.offerData && this.offerData.id
      if (!offerId) return
      // cvOtherMixin reaches this modal with a VIRTUAL offer (`temp_<ts>`): the
      // position is real, the offer row is not. Generating against it is a
      // guaranteed error and the poller it starts never terminates.
      if (!isRealOfferId(offerId)) return
      // Checked BEFORE the bookkeeping: on a cold store `generate` early-returns
      // on this same flag, and marking the mode as generated would then make the
      // flags handler skip the one call that was actually supposed to happen.
      if (this.$store.getters['cvTextArtifacts/aiTextsEnabled'] !== true) return
      this.generatedFor = mode
      this.$store.dispatch('cvTextArtifacts/generate', { offerId: offerId, mode: mode, vm: this })
    },
    async confirmSelection () {
      if (!this.selectedMode) {
        return
      }

      try {
        const completeOfferData = await this.loadCompleteOfferData()

        // Set before `remove`, because that is what destroys this component:
        // the session belongs to the CV modal from here on. Left false when
        // loadCompleteOfferData throws — that path stays an abandonment.
        this.handedOff = true
        this.remove(this.$parent.payload)

        this.openCvModal(this.selectedMode, completeOfferData)
      } catch (error) {
        console.error('Error loading complete offer data:', error)
        this.$utils.emit('show_toast', { text: 'שגיאה בטעינת נתוני ההצעה', type: 'error' })
      }
    },
    async loadCompleteOfferData () {
      const currentOffer = this.offerData

      if (currentOffer &&
          currentOffer.position &&
          currentOffer.position.company &&
          currentOffer.position.company.resource_id) {
        return currentOffer
      }

      if (!currentOffer || !currentOffer.position || !currentOffer.position.id) {
        throw new Error('Missing position ID in offer data')
      }

      const positionId = currentOffer.position.id
      const completePosition = await this.$network_utils.getPosition({ id: positionId })

      return {
        ...currentOffer,
        position: completePosition
      }
    },
    openCvModal (mode, offerData) {
      let modalConfig = {
        data: offerData || this.offerData,
        modal_title: 'Send CV'
      }

      // R-7: with CV_PATTERN_UI_ENABLED on, normal/lite keep their in-modal
      // רגיל/לייט selector so she can flip without reopening. With the flag off
      // the selector stays hidden — the pre-feature behavior, byte-for-byte.
      const hideModeSelector = !this.patternUiEnabled

      switch (mode) {
        case 'normal':
          modalConfig.designator = 'cv-modal-normal'
          modalConfig.component_name = 'CVModalForm'
          modalConfig.modal_title = 'Send CV (Normal)'
          modalConfig.props = { initialMode: 'normal', hideModeSelector: hideModeSelector }
          break
        case 'lite':
          modalConfig.designator = 'cv-modal-lite'
          modalConfig.component_name = 'CVModalForm'
          modalConfig.modal_title = 'Send CV (Lite)'
          modalConfig.props = { initialMode: 'lite', hideModeSelector: hideModeSelector }
          break
        case 'super_lite':
          modalConfig.designator = 'cv-modal-super-lite'
          modalConfig.component_name = 'CVModalSuperLiteForm'
          modalConfig.modal_title = 'Send CV'
          break
      }

      this.create(modalConfig)
    },
    cancel () {
      this.remove(this.$parent.payload)
    },
    ...mapMutations('modals', ['create', 'remove'])
  }
}
</script>

<style scoped>
.send-cv-mode-selection-modal {
  padding: 24px;
  direction: rtl;
  text-align: right;
  width: 95vw;
  max-width: 1600px;
  min-width: 1200px;
  height: 85vh;
  max-height: 900px;
  background-color: #f8f9fa;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  left: 50%;
  transform: translateX(-50%);
}

/*
  Modal.vue injects `modal_content` (max-height:80vh; overflow-y:auto; padding:30px)
  onto the SAME root element as `.send-cv-mode-selection-modal` (85vh, its own
  overflow). Equal specificity, so bundle order decided the winner — which is not
  a contract. Neutralize it explicitly for this designator (UI reviewer issue 4).
*/
.send-cv-mode-selection-modal.modal_content {
  max-height: none;
  overflow: hidden;
  padding: 24px;
}

.modal-content-wrapper {
  display: flex;
  flex-direction: row;
  gap: 32px;
  align-items: flex-start;
  height: 100%;
  background-color: #f8f9fa;
}

.main-content {
  flex: 0 0 30%;
  min-width: 350px;
  height: 100%;
  display: flex;
  flex-direction: column;
  /* R-5: without these the explanation block could push `פתח טופס שליחה` out of
     a fixed-height modal with no way to scroll to it (E-16). */
  overflow-y: auto;
  overscroll-behavior: contain;
  background-color: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.sidebar-content {
  flex: 0 0 70%;
  min-width: 600px;
  height: 100%;
}

.evaluation-section {
  background-color: #ffffff;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
  text-align: right;
  height: 100%;
  overflow-y: auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.evaluation-title {
  font-size: 18px;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 16px;
  margin-top: 0;
  text-align: center;
}

.evaluation-content {
  direction: ltr;
}

/* Override styles for ModalEvaluate component when used in this modal */
.evaluation-content >>> .table {
  font-size: 12px;
  margin-bottom: 0;
}

.evaluation-content >>> .table th,
.evaluation-content >>> .table td {
  padding: 8px;
  vertical-align: middle;
}

.evaluation-content >>> .table th {
  font-size: 11px;
  font-weight: 600;
  background-color: #e9ecef;
}

.evaluation-content >>> .box {
  padding: 8px;
  margin-bottom: 8px;
}

.evaluation-content >>> .button {
  padding: 4px 8px;
  font-size: 10px;
  height: auto;
}

.evaluation-content >>> .dropdown-trigger {
  font-size: 11px;
}

.evaluation-content >>> .textarea {
  font-size: 11px;
}

.evaluation-content >>> .is-size-7 {
  font-size: 10px !important;
}

.evaluation-content >>> .is-size-3 {
  font-size: 16px !important;
}

.no-data {
  text-align: center;
  color: #7f8c8d;
  font-style: italic;
  padding: 20px 0;
}

.modal-header {
  text-align: center;
  margin-bottom: 32px;
}

.modal-title {
  font-size: 24px;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 8px;
}

.modal-subtitle {
  font-size: 16px;
  color: #7f8c8d;
  margin: 0;
}

.mode-options {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
}

/* 8px to the card stack, 32px to the CTA row — proximity alone groups the
   explanation with the cards it explains, so no divider is needed. */
.mode-options.has-reco {
  margin-bottom: 8px;
}

.mode-option {
  display: flex;
  align-items: center;
  padding: 20px;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #ffffff;
}

.mode-option:hover {
  border-color: #3498db;
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(52, 152, 219, 0.15);
}

.mode-option.selected {
  border-color: #3498db;
  background-color: #e3f2fd;
  box-shadow: 0 4px 12px rgba(52, 152, 219, 0.2);
}

.mode-icon {
  margin-left: 20px;
  font-size: 32px;
  color: #3498db;
  min-width: 50px;
  text-align: center;
}

.mode-option.selected .mode-icon {
  color: #2980b9;
}

.mode-content {
  flex: 1;
}

.mode-name {
  font-size: 20px;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 8px;
}

.mode-description {
  font-size: 14px;
  color: #7f8c8d;
  margin: 0;
  line-height: 1.4;
}

.mode-option.selected .mode-name {
  color: #2980b9;
}

.mode-option.selected .mode-description {
  color: #34495e;
}

.mode-option.default {
  border-color: #f39c12;
  background-color: #fef9e7;
}

.mode-option.default .mode-icon {
  color: #f39c12;
}

.default-badge {
  display: inline-block;
  background-color: #f39c12;
  /* The badge is now the load-bearing recommendation signal, so it has to pass
     AA: white on #f39c12 was 2.19:1, #212529 is 7.0:1 (UI reviewer issue 6). */
  color: #212529;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  margin-right: 8px;
  vertical-align: middle;
}

.mode-option.selected.default {
  border-color: #3498db;
  background-color: #e3f2fd;
}

.mode-option.selected.default .default-badge {
  background-color: #2980b9;
}

.modal-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 32px;
  /* Sticky, so a long explanation can never make the primary CTA unreachable. */
  position: sticky;
  bottom: 0;
  z-index: 2;
  padding: 12px 0 4px;
  background-color: #ffffff;
  box-shadow: 0 -8px 12px -8px rgba(0, 0, 0, 0.12);
}

.button {
  min-width: 160px;
  height: 48px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.button.is-primary {
  background-color: #3498db;
  border-color: #3498db;
}

.button.is-primary:hover:not(:disabled) {
  background-color: #2980b9;
  border-color: #2980b9;
  transform: translateY(-1px);
}

.button.is-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.button.is-light {
  background-color: #ecf0f1;
  border-color: #bdc3c7;
  color: #2c3e50;
}

.button.is-light:hover {
  background-color: #d5dbdb;
  border-color: #95a5a6;
  transform: translateY(-1px);
}

.icon {
  margin-left: 8px;
}

/* Responsive design */
@media (max-width: 1300px) {
  .send-cv-mode-selection-modal {
    width: 95vw;
    min-width: 800px;
  }

  .modal-content-wrapper {
    flex-direction: column;
    height: 100%;
    overflow-y: auto;
  }

  .sidebar-content {
    width: 100%;
    order: -1;
  }

  /* Stacked columns: the WRAPPER scrolls, not each column, otherwise the sticky
     CTA sticks to a container that never scrolls. */
  .main-content,
  .sidebar-content {
    width: 100%;
    height: auto;
    min-height: 0;
    overflow-y: visible;
  }

  .evaluation-section {
    height: auto;
    max-height: 60vh;
  }
}

@media (max-width: 900px) {
  .send-cv-mode-selection-modal {
    width: 95vw;
    min-width: 600px;
  }
}

@media (max-width: 768px) {
  .send-cv-mode-selection-modal {
    padding: 16px;
    width: 95vw;
    min-width: 320px;
  }

  .sidebar-content {
    width: 100%;
  }

  .mode-option {
    padding: 16px;
  }

  .mode-icon {
    font-size: 24px;
    margin-left: 16px;
    min-width: 40px;
  }

  .mode-name {
    font-size: 18px;
  }

  .modal-actions {
    flex-direction: column;
  }

  .button {
    width: 100%;
  }
}
</style>
